import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  CheckSquare,
  Loader2,
  Search,
  UserRound
} from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Sidebar } from '../components/Sidebar'
import {
  buildCompletedOperationsTaskTodos,
  buildOperationsTaskItems,
  filterOperationsTaskItems,
  type OperationsDashboardCall,
  type OperationsTaskAssigneeFilter,
  type OperationsTaskDueFilter,
  type OperationsTaskDueState,
  type OperationsTaskItem
} from '../lib/operationsDashboard'
import { hasInternalApi, internalApiGet, internalApiPatch } from '../lib/internalApi'
import { supabase } from '../lib/supabase'
import { useLivers } from '../lib/useLivers'

export function TasksPage() {
  const qc = useQueryClient()
  const livers = useLivers()
  const calls = useQuery<OperationsDashboardCall[]>({
    queryKey: ['operations-dashboard', 'calls'],
    queryFn: async () => {
      if (hasInternalApi()) {
        const data = await internalApiGet<{ calls: OperationsDashboardCall[] }>('/api/calls?limit=1000')
        return data.calls
      }

      const { data, error } = await supabase
        .from('call_records')
        .select('id,liver_id,called_at,duration_seconds,status,todos,next_follow_date,summary,error_message,updated_at')
        .order('called_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as OperationsDashboardCall[]
    }
  })
  const completeTask = useMutation({
    mutationFn: async (item: OperationsTaskItem) => {
      const call = (calls.data ?? []).find((candidate) => candidate.id === item.callId)
      if (!call) throw new Error('対象の通話が見つかりません。画面を更新してください。')

      const todos = buildCompletedOperationsTaskTodos(call, item)
      if (hasInternalApi()) {
        await internalApiPatch(`/api/calls/${item.callId}`, { todos })
        return
      }

      const { error } = await supabase
        .from('call_records')
        .update({ todos })
        .eq('id', item.callId)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operations-dashboard', 'calls'] })
    }
  })
  const items = buildOperationsTaskItems(calls.data ?? [], livers.data ?? [], new Date())
  const [searchText, setSearchText] = useState('')
  const [assigneeFilter, setAssigneeFilter] = useState<OperationsTaskAssigneeFilter>('all')
  const [dueFilter, setDueFilter] = useState<OperationsTaskDueFilter>('all')
  const filteredItems = filterOperationsTaskItems(items, {
    searchText,
    assignee: assigneeFilter,
    dueState: dueFilter
  })
  const overdueCount = items.filter((item) => item.dueState === 'overdue').length
  const todayCount = items.filter((item) => item.dueState === 'today').length
  const loading = calls.isLoading || livers.isLoading
  const error = calls.error ?? livers.error

  return (
    <div className="flex min-h-full">
      <Sidebar />
      <main className="min-w-0 flex-1 bg-gray-50 px-4 py-6 pt-16 sm:px-6 md:pt-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">TODO</h1>
              <p className="mt-1 text-sm leading-6 text-gray-500">
                通話から抽出された未完了TODOを、ライバー横断で確認します。
              </p>
            </div>
            {loading && <p className="text-xs text-gray-400">集計中...</p>}
            {error && (
              <p className="text-xs text-red-600">
                TODOの取得に失敗: {error instanceof Error ? error.message : String(error)}
              </p>
            )}
            {completeTask.error && (
              <p className="text-xs text-red-600">
                TODOの更新に失敗: {completeTask.error instanceof Error ? completeTask.error.message : String(completeTask.error)}
              </p>
            )}
          </header>

          <section className="grid gap-3 sm:grid-cols-3">
            <TaskMetric label="未完了" value={`${items.length}件`} tone="slate" />
            <TaskMetric label="期限超過" value={`${overdueCount}件`} tone="red" />
            <TaskMetric label="本日期限" value={`${todayCount}件`} tone="sky" />
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white">
            <div className="flex flex-col gap-4 border-b border-gray-200 px-4 py-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">未完了TODO一覧</h2>
                <p className="mt-1 text-xs text-gray-500">
                  期限超過 → 本日期限 → 近日 → 期限なし の順で並びます。検索や担当で絞り込めます。
                </p>
              </div>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="search"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="TODO / ライバー / 根拠を検索"
                    className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 outline-none transition placeholder:text-gray-400 focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {TASK_ASSIGNEE_FILTERS.map((filter) => (
                    <button
                      key={filter.value}
                      type="button"
                      onClick={() => setAssigneeFilter(filter.value)}
                      className={`rounded-lg px-3 py-2 text-xs font-medium transition ${
                        assigneeFilter === filter.value ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                  {TASK_DUE_FILTERS.map((filter) => (
                    <button
                      key={filter.value}
                      type="button"
                      onClick={() => setDueFilter(filter.value)}
                      className={`rounded-lg px-3 py-2 text-xs font-medium transition ${
                        dueFilter === filter.value ? 'bg-sky-600 text-white' : 'bg-sky-50 text-sky-700 hover:bg-sky-100'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
                <span className="w-fit rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">
                  {filteredItems.length}/{items.length}件
                </span>
              </div>
            </div>
            {loading ? (
              <p className="px-4 py-8 text-sm text-gray-400">読み込み中...</p>
            ) : filteredItems.length > 0 ? (
              <ul className="divide-y divide-gray-100">
                {filteredItems.map((item) => (
                  <TaskRow
                    key={item.id}
                    item={item}
                    completing={completeTask.isPending && completeTask.variables?.id === item.id}
                    onComplete={(target) => completeTask.mutate(target)}
                  />
                ))}
              </ul>
            ) : items.length === 0 ? (
              <div className="flex items-start gap-3 px-4 py-8">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900">未完了TODOはありません</p>
                  <p className="mt-1 text-xs text-gray-500">
                    新しい通話やLINE履歴からTODOが抽出されると、ここに表示されます。
                  </p>
                </div>
              </div>
            ) : (
              <div className="px-4 py-8">
                <p className="text-sm font-medium text-gray-900">条件に一致するTODOはありません</p>
                <p className="mt-1 text-xs text-gray-500">
                  検索語やフィルターを外すと、他の未完了TODOを確認できます。
                </p>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  )
}

function TaskMetric({ label, value, tone }: { label: string; value: string; tone: MetricTone }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${METRIC_TONE[tone]}`}>
          <CheckSquare className="h-4 w-4" />
        </span>
      </div>
      <p className="mt-4 text-2xl font-semibold tabular-nums text-gray-900">{value}</p>
    </div>
  )
}

function TaskRow({
  item,
  completing,
  onComplete
}: {
  item: OperationsTaskItem
  completing: boolean
  onComplete: (item: OperationsTaskItem) => void
}) {
  return (
    <li className="flex flex-col gap-3 px-4 py-4 transition hover:bg-gray-50 sm:flex-row sm:items-start">
      <Link
        to={`/liver/${item.liverId}`}
        className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start"
      >
        <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${DUE_TONE[item.dueState]}`}>
          {item.dueState === 'overdue' ? <AlertCircle className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${ASSIGNEE_TONE[item.assignee]}`}>
              {item.assignee === 'manager' ? 'マネ' : 'ライバー'}
            </span>
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${DUE_BADGE_TONE[item.dueState]}`}>
              {dueLabel(item)}
            </span>
            <span className="text-xs text-gray-400">{formatShortDate(item.calledAt)} の通話から</span>
          </div>
          <p className="mt-2 break-words text-sm font-medium text-gray-900">{item.task}</p>
          {item.evidence && (
            <p className="mt-1 line-clamp-2 text-xs leading-5 text-gray-500">根拠: {item.evidence}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1">
              <UserRound className="h-3.5 w-3.5" />
              {item.liverName}
            </span>
            {item.confidence != null && (
              <span>信頼度 {Math.round(item.confidence * 100)}%</span>
            )}
          </div>
        </div>
        <ArrowRight className="hidden h-4 w-4 shrink-0 text-gray-300 sm:mt-3 sm:block" />
      </Link>
      <button
        type="button"
        onClick={() => onComplete(item)}
        disabled={completing}
        className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 sm:mt-2"
      >
        {completing ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <CheckCircle2 className="h-3.5 w-3.5" />
        )}
        完了
      </button>
    </li>
  )
}

type MetricTone = 'slate' | 'red' | 'sky'

const TASK_ASSIGNEE_FILTERS: Array<{ value: OperationsTaskAssigneeFilter; label: string }> = [
  { value: 'all', label: '全担当' },
  { value: 'manager', label: 'マネ担当' },
  { value: 'liver', label: 'ライバー担当' }
]

const TASK_DUE_FILTERS: Array<{ value: OperationsTaskDueFilter; label: string }> = [
  { value: 'all', label: '全期限' },
  { value: 'overdue', label: '期限超過' },
  { value: 'today', label: '今日' },
  { value: 'none', label: '期限なし' }
]

const METRIC_TONE: Record<MetricTone, string> = {
  slate: 'bg-slate-100 text-slate-700',
  red: 'bg-red-50 text-red-700',
  sky: 'bg-sky-50 text-sky-700'
}

const DUE_TONE: Record<OperationsTaskDueState, string> = {
  overdue: 'bg-red-50 text-red-700',
  today: 'bg-sky-50 text-sky-700',
  upcoming: 'bg-emerald-50 text-emerald-700',
  none: 'bg-slate-100 text-slate-600'
}

const DUE_BADGE_TONE: Record<OperationsTaskDueState, string> = {
  overdue: 'bg-red-50 text-red-700',
  today: 'bg-sky-50 text-sky-700',
  upcoming: 'bg-emerald-50 text-emerald-700',
  none: 'bg-gray-100 text-gray-500'
}

const ASSIGNEE_TONE = {
  manager: 'bg-sky-100 text-sky-700',
  liver: 'bg-rose-100 text-rose-700'
} as const

function dueLabel(item: OperationsTaskItem): string {
  if (!item.dueDate) return '期限なし'
  if (item.dueState === 'overdue') return `期限超過 ${formatShortDate(item.dueDate)}`
  if (item.dueState === 'today') return '本日期限'
  return `期限 ${formatShortDate(item.dueDate)}`
}

function formatShortDate(value: string): string {
  const date = value.includes('T') ? new Date(value) : new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('ja-JP', {
    month: 'numeric',
    day: 'numeric'
  })
}
